import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import {
  FlipHorizontal,
  Upload,
  Play,
  Link as LinkIcon,
  Gauge,
  Repeat,
} from "lucide-react";

interface HeroSectionProps {
  videoUrl: string;
  setVideoUrl: (url: string) => void;
  handleUrlSubmit: () => void;
  handleFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onExampleClick: (id: string, title: string) => void;
  isAuthenticated?: boolean;
}

const EXAMPLE_VIDEOS = [
  { id: "A1nu9jg6ciY", title: "BTS (방탄소년단) '2.0' Official MV", img: "https://img.youtube.com/vi/A1nu9jg6ciY/mqdefault.jpg" },
  { id: "NYUkIJ-wcCs", title: "River Novin choreography to \"No Hands\" by Roscoe Dash + Waka Flocka Flame at Offstage Dance Studio", img: "https://img.youtube.com/vi/NYUkIJ-wcCs/mqdefault.jpg" },
  { id: "gSm-eTRl9JE", title: "CORTIS (코르티스) 'GO!' Dance Practice (Fix ver.)", img: "https://img.youtube.com/vi/gSm-eTRl9JE/mqdefault.jpg" },
  { id: "OVSkSepLEDA", title: "[1st Place] Brotherhood Round 2 Front Row | IDL 2026 Seoul Series", img: "https://img.youtube.com/vi/OVSkSepLEDA/mqdefault.jpg" },
];

export function HeroSection({
  videoUrl,
  setVideoUrl,
  handleUrlSubmit,
  handleFileUpload,
  fileInputRef,
  onExampleClick,
  isAuthenticated = false,
}: HeroSectionProps) {
  return (
    <div className="max-w-2xl mx-auto space-y-6 sm:space-y-8">
      <div className="text-center space-y-4 sm:space-y-6 py-6 sm:py-10">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
          <FlipHorizontal className="w-4 h-4" />
          Mirror any dance video instantly
        </div>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
          Stop Dancing <span className="text-primary">Backwards</span>
        </h2>
        <p className="text-muted-foreground text-base sm:text-lg lg:text-xl max-w-xl mx-auto px-2">
          Mirror any dance video so left and right finally make sense.
          When you face a dancer on screen, their left is your right.
        </p>
        <p className="text-muted-foreground text-base sm:text-lg lg:text-xl max-w-xl mx-auto px-2">
          <span className="text-primary font-bold">DanceMirror</span> flips videos instantly so you can follow dance moves naturally, learn faster, and stop second-guessing your movements.
        </p>
      </div>

      <div className="space-y-6 sm:space-y-8 text-center">
        <div className="py-6 sm:py-8" data-testid="mirror-demo" aria-hidden="true">
          <div className="relative mx-auto" style={{ maxWidth: '340px' }}>
            <div className="relative">
              <div className="mirror-demo-frame rounded-md overflow-hidden">
                <div className="aspect-video bg-gradient-to-b from-muted/20 to-muted/50 flex items-center justify-center relative">
                  <span className="text-5xl sm:text-6xl">💃</span>
                </div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 px-2 pb-1.5 z-10 pointer-events-none">
                <div className="h-1 rounded-full bg-foreground/10">
                  <div className="h-full rounded-full bg-primary" style={{ animation: 'mirror-progress 6s linear infinite' }} />
                </div>
              </div>
              <div className="absolute inset-0 pointer-events-none">
                <div className="mirror-demo-original absolute inset-0 flex items-center justify-between px-3 sm:px-4">
                  <span className="px-2 py-1 rounded-md text-xs sm:text-sm font-bold bg-muted text-muted-foreground">R</span>
                  <span className="px-2 py-1 rounded-md text-xs sm:text-sm font-bold bg-muted text-muted-foreground">L</span>
                </div>
                <div className="mirror-demo-mirrored absolute inset-0 flex items-center justify-between px-3 sm:px-4">
                  <span className="px-2 py-1 rounded-md text-xs sm:text-sm font-bold bg-primary/20 text-primary">L</span>
                  <span className="px-2 py-1 rounded-md text-xs sm:text-sm font-bold bg-primary/20 text-primary">R</span>
                </div>
              </div>
            </div>
            <div className="relative h-7 mt-3 text-center">
              <p className="mirror-demo-original absolute inset-0 text-sm text-muted-foreground flex items-center justify-center gap-2">
                Their left ≠ Your left
              </p>
              <p className="mirror-demo-mirrored absolute inset-0 text-sm text-primary font-medium flex items-center justify-center gap-2">
                <FlipHorizontal className="w-4 h-4" />
                Now follow along naturally
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 pt-2">
          <Card className="p-3 sm:p-4 text-center space-y-1 sm:space-y-2">
            <FlipHorizontal className="w-6 h-6 sm:w-8 sm:h-8 mx-auto text-primary" />
            <h3 className="font-medium text-sm sm:text-base">Mirror Mode</h3>
            <p className="text-xs sm:text-sm text-muted-foreground hidden sm:block">
              Flip the video to match your movements
            </p>
          </Card>
          <Card className="p-3 sm:p-4 text-center space-y-1 sm:space-y-2">
            <div className="flex justify-center gap-2">
              <span className="text-lg sm:text-2xl font-bold text-primary">L</span>
              <span className="text-lg sm:text-2xl font-bold text-primary">R</span>
            </div>
            <h3 className="font-medium text-sm sm:text-base">L/R Overlay</h3>
            <p className="text-xs sm:text-sm text-muted-foreground hidden sm:block">
              Never mix up left and right again
            </p>
          </Card>
          <Card className="p-3 sm:p-4 text-center space-y-1 sm:space-y-2">
            <Repeat className="w-6 h-6 sm:w-8 sm:h-8 mx-auto text-primary" />
            <h3 className="font-medium text-sm sm:text-base">A-B Loop</h3>
            <p className="text-xs sm:text-sm text-muted-foreground hidden sm:block">
              Loop any section to nail the moves
            </p>
          </Card>
          <Card className="p-3 sm:p-4 text-center space-y-1 sm:space-y-2">
            <Gauge className="w-6 h-6 sm:w-8 sm:h-8 mx-auto text-primary" />
            <h3 className="font-medium text-sm sm:text-base">Speed Control</h3>
            <p className="text-xs sm:text-sm text-muted-foreground hidden sm:block">
              Slow it down to learn every detail
            </p>
          </Card>
        </div>
      </div>

      <div className="space-y-3">
        <p className="text-sm text-muted-foreground text-center">Try with a popular dance — one click to start practicing:</p>
        <div className="grid grid-cols-2 gap-2 sm:gap-3">
          {EXAMPLE_VIDEOS.map((example) => (
            <button
              key={example.id}
              onClick={() => {
                onExampleClick(example.id, example.title);
              }}
              className="group relative rounded-lg overflow-hidden border border-border hover:border-primary/50 transition-colors"
              data-testid={`button-example-${example.id}`}
            >
              <img
                src={example.img}
                alt={example.title}
                className="w-full aspect-video object-cover group-hover:scale-105 transition-transform duration-200"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-2">
                <span className="text-white text-[10px] sm:text-xs font-medium leading-tight line-clamp-2">{example.title}</span>
              </div>
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="bg-primary/90 rounded-full p-2">
                  <Play className="w-4 h-4 text-white fill-white" />
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="relative" id="get-started">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-border" />
        </div>
        <div className="relative flex justify-center">
          <span className="bg-background px-4 text-sm text-muted-foreground">Or paste your own</span>
        </div>
      </div>

      <Card className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        <div className="space-y-2 sm:space-y-3">
          <p className="text-sm text-muted-foreground">
            Paste a YouTube dance video link to start practicing instantly.
          </p>
          <label className="text-sm font-medium flex items-center gap-2">
            <LinkIcon className="w-4 h-4" />
            YouTube URL
          </label>
          <div className="flex gap-2">
            <Input
              type="url"
              placeholder="https://youtube.com/watch?v=..."
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleUrlSubmit()}
              className="text-base"
              data-testid="input-youtube-url"
            />
            <Button onClick={handleUrlSubmit} disabled={!videoUrl} data-testid="button-load-video">
              Load
            </Button>
          </div>
        </div>

        <div className="relative flex items-center gap-4">
          <div className="flex-1 border-t border-border" />
          <span className="text-sm text-muted-foreground">or</span>
          <div className="flex-1 border-t border-border" />
        </div>

        <div className="space-y-2 sm:space-y-3">
          <p className="text-sm text-muted-foreground">
            Upload your own video to mirror.
          </p>
          <label className="text-sm font-medium flex items-center gap-2">
            <Upload className="w-4 h-4" />
            Upload Video File
          </label>
          <div
            className="border-2 border-dashed rounded-md p-6 sm:p-8 text-center cursor-pointer hover-elevate transition-colors"
            onClick={() => fileInputRef.current?.click()}
            data-testid="dropzone-upload"
          >
            <Upload className="w-8 h-8 sm:w-10 sm:h-10 mx-auto mb-2 sm:mb-3 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Tap to upload
            </p>
          </div>
          <p className="text-xs text-muted-foreground">
            {isAuthenticated
              ? "MP4, MOV, WebM · max 4 GB"
              : "MP4, MOV, WebM · max 500 MB (sign in for up to 4 GB)"}
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            className="hidden"
            onChange={handleFileUpload}
            data-testid="input-file-upload"
          />
        </div>
      </Card>
    </div>
  );
}
